import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';

import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/styles.css';
import '../styles/formularios.css';

import logoInstituto from "../components/images/logo.png";
import logoSEP from "../components/images/SEP_Logo_2019.png";
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveApiBaseList } from '../utils/apiBase';


const RegistrarAlumnos = () => {
  const navigate = useNavigate();
    const location = useLocation();
    const handleSalir = () => {
      const rol = resolveRolBase(location.pathname);
      if (rol) {
        navigate(`/${rol}/inicio`, { replace: true });
      } else {
        // Fallback por si no se detecta rol ni en path ni en localStorage:
        navigate('/inicio', { replace: true });
      }
    };

  // Estado de campos
  const [idAlumno, setIdAlumno] = useState('Auto');
  const [nombre, setNombre] = useState('');
  const [apellidoPaterno, setApellidoPaterno] = useState('');
  const [apellidoMaterno, setApellidoMaterno] = useState('');
  const [curp, setCurp] = useState('');
  const [sexo, setSexo] = useState('');          // M | F
  const [correo, setCorreo] = useState('');
  const [tipoSangre, setTipoSangre] = useState('');
  const [telefono, setTelefono] = useState('');

  const [loading, setLoading] = useState(false);

  const formRef = useRef(null);
  const apiBases = useMemo(() => resolveApiBaseList().map((base) => `${base}/alumnos`), []);

  const fetchJsonWithFallback = useCallback(async (pathSuffix, options = {}) => {
    let lastError = null;
    for (const base of apiBases) {
      const url = `${base}${pathSuffix}`;
      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          const error = new Error(text || `Error ${response.status}`);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló la petición a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        try {
          if (contentType.includes('application/json')) {
            const parsed = text ? JSON.parse(text) : {};
            return parsed;
          }

          const trimmed = text?.trim();
          if (trimmed && (trimmed.startsWith('{') || trimmed.startsWith('['))) {
            const parsed = JSON.parse(trimmed);
            return parsed;
          }

          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`⚠️ Contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        } catch (parseError) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`⚠️ No se pudo parsear JSON de ${url}:`, parseError);
        }
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const postWithFallback = useCallback(async (pathSuffix, payload) => {
    let lastError = null;
    for (const base of apiBases) {
      const url = `${base}${pathSuffix}`;
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const text = await response.text();

        if (!response.ok) {
          const error = new Error(text || `Error ${response.status}`);
          error.status = response.status;
          lastError = error;
          console.warn(`⚠️ Falló el POST a ${url}:`, error);
          continue;
        }

        const trimmed = text.trim();
        if (trimmed && trimmed.startsWith('<')) {
          const error = new Error(`Respuesta inválida desde ${url}`);
          lastError = error;
          console.warn(`⚠️ Contenido no válido desde ${url}`);
          continue;
        }

        return { text: trimmed, url };
      } catch (error) {
        lastError = error;
        console.warn(`⚠️ Error de red al hacer POST ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  const cargarSiguienteIdAlumno = useCallback(async () => {
    try {
      const data = await fetchJsonWithFallback('/siguiente-id');
      setIdAlumno(data?.siguienteId ?? 'Auto');
    } catch (e) {
      console.error('❌ Error al cargar el siguiente ID del alumno:', e);
      setIdAlumno('Error');
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => {
    cargarSiguienteIdAlumno();
  }, [cargarSiguienteIdAlumno]);

  const onSubmit = async (e) => {
    e.preventDefault();

    if (formRef.current && !formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      return;
    }

    const curpUp = (curp || '').trim().toUpperCase();
    const tel = (telefono || '').trim();

    if (!/^[A-Z0-9]{18}$/.test(curpUp)) {
      await Swal.fire('Atención', 'CURP inválida. Debe contener 18 caracteres alfanuméricos en mayúsculas.', 'warning');
      return;
    }

    if (!/^\d{10}$/.test(tel)) {
      await Swal.fire('Atención', 'Teléfono inválido. Debe tener 10 dígitos numéricos.', 'warning');
      return;
    }

    const payload = {
      nombre: (nombre || '').trim(),
      apellidoPaterno: (apellidoPaterno || '').trim(),
      apellidoMaterno: (apellidoMaterno || '').trim(),
      curp: curpUp,
      sexo,
      telefono: parseInt(tel, 10),
      correo: (correo || '').trim(),
      tipoSangre
    };

    setLoading(true);
    try {
      const { text } = await postWithFallback('/registrar', payload);

      await Swal.fire({
        icon: 'success',
        title: 'Registrado',
        text: text || 'Alumno registrado correctamente',
        showConfirmButton: false,
        timer: 2000
      });

      setNombre('');
      setApellidoPaterno('');
      setApellidoMaterno('');
      setCurp('');
      setSexo('');
      setCorreo('');
      setTipoSangre('');
      setTelefono('');
      await cargarSiguienteIdAlumno();
    } catch (error) {
      console.error('❌ Error al registrar al alumno:', error);
      await Swal.fire('Error', `❌ ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Registrar Alumnos</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Formulario */}
      <div className="p-4 bg-light rounded shadow-sm">
        <form id="formAlumno" className="row g-3" ref={formRef} noValidate onSubmit={onSubmit}>
          <div className="col-md-3">
            <label htmlFor="idAlumno" className="form-label">ID Alumno</label>
            <input type="text" className="form-control" id="idAlumno" value={idAlumno} disabled />
          </div>

          <div className="col-md-3">
            <label htmlFor="tipoUsuario" className="form-label">Tipo de Usuario</label>
            <input type="text" className="form-control" id="tipoUsuario" value="Alumno" disabled />
          </div>

          <div className="col-md-6">
            <label htmlFor="nombre" className="form-label">Nombre</label>
            <input
              type="text"
              className="form-control"
              id="nombre"
              maxLength={30}
              pattern="[A-Za-zÁÉÍÓÚáéíóúÑñ\s]+"
              required
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="apellidoPaterno" className="form-label">Apellido Paterno</label>
            <input
              type="text"
              className="form-control"
              id="apellidoPaterno"
              maxLength={30}
              pattern="[A-Za-zÁÉÍÓÚáéíóúÑñ\s]+"
              required
              value={apellidoPaterno}
              onChange={(e) => setApellidoPaterno(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="apellidoMaterno" className="form-label">Apellido Materno</label>
            <input
              type="text"
              className="form-control"
              id="apellidoMaterno"
              maxLength={30}
              pattern="[A-Za-zÁÉÍÓÚáéíóúÑñ\s]+"
              required
              value={apellidoMaterno}
              onChange={(e) => setApellidoMaterno(e.target.value)}
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="curp" className="form-label">CURP</label>
            <input
              type="text"
              className="form-control"
              id="curp"
              maxLength={18}
              pattern="[A-Z0-9]{18}"
              required
              onChange={(e) => setCurp(e.target.value.toUpperCase())}
            />
          </div>

          <div className="col-md-2">
            <label htmlFor="sexo" className="form-label">Sexo</label>
            <select
              id="sexo"
              className="form-select"
              required
              value={sexo}
              onChange={(e) => setSexo(e.target.value)}
            >
              <option value="" disabled>Seleccionar</option>
              <option value="M">M</option>
              <option value="F">F</option>
            </select>
          </div>

          <div className="col-md-6">
            <label htmlFor="correo" className="form-label">Correo</label>
            <input
              type="email"
              className="form-control"
              id="correo"
              maxLength={50}
              required
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
            />
          </div>

          <div className="col-md-3">
            <label htmlFor="tipoSangre" className="form-label">Tipo de Sangre</label>
            <select
              id="tipoSangre"
              className="form-select"
              required
              value={tipoSangre}
              onChange={(e) => setTipoSangre(e.target.value)}
            >
              <option value="" disabled>Seleccionar</option>
              <option>A+</option>
              <option>A-</option>
              <option>B+</option>
              <option>B-</option>
              <option>AB+</option>
              <option>AB-</option>
              <option>O+</option>
              <option>O-</option>
            </select>
          </div>

          <div className="col-md-3">
            <label htmlFor="telefono" className="form-label">Teléfono</label>
            <input
              type="text"
              className="form-control"
              id="telefono"
              pattern="^[0-9]{10}$"
              maxLength={10}
              required
              value={telefono}
              onChange={(e) => setTelefono(e.target.value.replace(/\D/g, ''))}
            />
          </div>

          {/* Botones */}
          <div className="d-flex justify-content-center gap-3 mt-4">
            <button id="btnRegistrar" className="btn btn-success px-4" onClick={onSubmit} disabled={loading}>
              {loading ? 'Guardando...' : 'Registrar'}
            </button>
            {/*  Salir al inicio del rol actual (sin cerrar sesión) */}
            <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
             Salir
            </button>          
            </div>
        </form>
      </div>
    </div>
  );
};
/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}
export default RegistrarAlumnos;
